import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  fetchExternalApplications,
  settleCredit,
  updateCreditLimit,
} from "../../Apis/Wallet-api";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import { formatMoney } from "../../Utils/walletFormat";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import { isWalletAgentUser, isWalletClientUser } from "../../Utils/walletAdminAccess";

function normalizeWalletId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
}

export default function CreditManagementPage() {
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const isClientUser = isWalletClientUser(userData);
  const isAgentUser = isWalletAgentUser(userData);
  const canManageCredit = hasPermissionAction(
    Permission,
    "WALLET_CREDIT_MANAGE||WALLET_ADMIN_ALL"
  );

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletId, setWalletId] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [settleAmount, setSettleAmount] = useState("");
  const [settleReference, setSettleReference] = useState("");

  const walletApps = useMemo(
    () => apps.filter((a) => a.wallet_id),
    [apps]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchExternalApplications();
      setApps(Array.isArray(rows) ? rows : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load wallets.");
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!walletId && walletApps.length === 1) {
      setWalletId(normalizeWalletId(walletApps[0].wallet_id));
    }
  }, [walletApps, walletId]);

  const selected = walletApps.find(
    (a) => normalizeWalletId(a.wallet_id) === normalizeWalletId(walletId)
  );

  useEffect(() => {
    if (selected) {
      setCreditLimit(selected.credit_limit ?? "");
    }
  }, [selected]);

  const creditAvailable = selected
    ? Math.max(
        0,
        Number(selected.credit_limit || 0) - Number(selected.credit_used || 0)
      )
    : 0;

  const saveLimit = async () => {
    if (!walletId) return;
    try {
      await updateCreditLimit(walletId, creditLimit);
      toast.success("Credit limit updated.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed.");
    }
  };

  const settle = async () => {
    if (!walletId) return;
    const userId =
      userData?.application_user_id ||
      userData?.organizationuser?.application_user_id;
    try {
      await settleCredit(walletId, settleAmount, settleReference, userId);
      toast.success("Settlement recorded.");
      setSettleAmount("");
      setSettleReference("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Settlement failed.");
    }
  };

  return (
    <WalletPageShell
      heading="Credit management"
      subtitle={
        isAgentUser
          ? "View credit limit and usage for your linked agent"
          : isClientUser
            ? "View credit limits and usage for your agents"
            : "Credit limits and settlements (credit used only — cash balance unchanged)"
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : walletApps.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {isAgentUser
              ? "No wallet linked to your agent yet. Contact an administrator."
              : isClientUser
                ? "No agents with wallets yet. Create an agent to manage credit."
                : "No wallets found. Create an external application first."}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Credit used</TableCell>
                  <TableCell align="right">Credit limit</TableCell>
                  <TableCell align="right">Available credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {walletApps.map((app) => {
                  const available = Math.max(
                    0,
                    Number(app.credit_limit || 0) - Number(app.credit_used || 0)
                  );
                  const rowWalletId = normalizeWalletId(app.wallet_id);
                  const isSelected =
                    rowWalletId === normalizeWalletId(walletId);

                  return (
                    <TableRow
                      key={app.id}
                      hover
                      selected={isSelected}
                      onClick={() => setWalletId(rowWalletId)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{app.name}</TableCell>
                      <TableCell align="right">{formatMoney(app.balance)}</TableCell>
                      <TableCell align="right">{formatMoney(app.credit_used)}</TableCell>
                      <TableCell align="right">{formatMoney(app.credit_limit)}</TableCell>
                      <TableCell align="right">{formatMoney(available)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {canManageCredit ? "Adjust selected wallet" : "Selected agent credit"}
            </Typography>

            {walletApps.length > 1 ? (
              <FormControl fullWidth size="small" sx={{ maxWidth: 360 }}>
                <InputLabel>Wallet / agent</InputLabel>
                <Select
                  label="Wallet / agent"
                  value={walletId || ""}
                  onChange={(e) =>
                    setWalletId(normalizeWalletId(e.target.value))
                  }
                >
                  <MenuItem value="">
                    <em>Select a wallet</em>
                  </MenuItem>
                  {walletApps.map((app) => (
                    <MenuItem
                      key={app.wallet_id}
                      value={normalizeWalletId(app.wallet_id)}
                    >
                      {app.name} (wallet #{app.wallet_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {selected
                  ? `${selected.name} (wallet #${selected.wallet_id})`
                  : walletApps[0]?.name
                    ? `${walletApps[0].name} (wallet #${walletApps[0].wallet_id})`
                    : "No wallet available"}
              </Typography>
            )}

            {selected ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Credit used
                  </Typography>
                  <Typography variant="body1">
                    {formatMoney(selected.credit_used)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Credit limit
                  </Typography>
                  <Typography variant="body1">
                    {formatMoney(selected.credit_limit)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Available credit
                  </Typography>
                  <Typography variant="body1">
                    {formatMoney(creditAvailable)}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Select an agent from the table above to view or adjust credit.
              </Typography>
            )}

            {canManageCredit && (
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ mt: 2 }}
                >
                  <TextField
                    size="small"
                    label="Credit limit"
                    type="number"
                    value={creditLimit}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    disabled={!selected}
                  />
                  <Button
                    variant="contained"
                    onClick={saveLimit}
                    disabled={!selected}
                  >
                    Update limit
                  </Button>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ mt: 2 }}
                >
                  <TextField
                    size="small"
                    label="Settlement amount"
                    type="number"
                    value={settleAmount}
                    onChange={(e) => setSettleAmount(e.target.value)}
                    disabled={!selected}
                  />
                  <TextField
                    size="small"
                    label="Reference"
                    value={settleReference}
                    onChange={(e) => setSettleReference(e.target.value)}
                    disabled={!selected}
                  />
                  <Button variant="outlined" onClick={settle} disabled={!selected}>
                    Settle credit
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        </Stack>
      )}
    </WalletPageShell>
  );
}
